import type { Baby, Call, CallOutcome, Mother } from '../types';
import { addDays, dayDiff, parseDate, startOfToday } from '../lib/dates';
import { loadClinic } from './store';

export type CallRange = 'today' | 'week' | 'month';
/** Where a call sits for staff: a person must act, the AI finished it, or it will be tried again. */
export type CallGroup = 'human' | 'handled' | 'retrying';

export interface CallRow {
  call: Call;
  mother: Mother;
  baby?: Baby;
  group: CallGroup;
  /** An open (not resolved) flag was raised from this call. */
  openFlag: boolean;
}

export interface SlotStat {
  slot: string; // e.g. "10–12"
  made: number;
  answered: number;
}

export interface DriftingMother {
  mother: Mother;
  baby?: Baby;
  missedInARow: number;
  lastTried: string;
}

export interface CallsOverview {
  made: number;
  answered: number;
  confirmed: number;
  flagged: number;
  notReached: number; // no answer, failed or waiting for a retry
  answerRate: number | null; // % of calls made that the mother picked up
  slots: SlotStat[];
  rows: CallRow[];
  drifting: DriftingMother[];
}

const RANGE_DAYS: Record<CallRange, number> = { today: 0, week: 6, month: 29 };
const NOT_REACHED: CallOutcome[] = ['no_answer', 'failed', 'retry_scheduled', 'in_progress'];
// Calling hours are 10:00–19:00 IST; slots match the morning batch and the evening retries.
const SLOTS: { slot: string; from: number; to: number }[] = [
  { slot: '10–12', from: 10, to: 12 },
  { slot: '12–14', from: 12, to: 14 },
  { slot: '14–17', from: 14, to: 17 },
  { slot: '17–19', from: 17, to: 19 },
];

export const isAnswered = (o: CallOutcome) => !NOT_REACHED.includes(o);

export function groupOf(outcome: CallOutcome, openFlag: boolean): CallGroup {
  if (openFlag || outcome === 'question_flagged' || outcome === 'failed') return 'human';
  if (outcome === 'no_answer' || outcome === 'retry_scheduled') return 'retrying';
  return 'handled';
}

/** Show only the last 4 digits of a phone number in lists, e.g. "••••• 0002". */
export const maskPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 4 ? `••••• ${digits.slice(-4)}` : '•••••';
};

/**
 * The Calls page: what the AI did, what still needs a person, and which mothers have stopped answering.
 * "Drifting" = the last two or more calls in a row went unanswered (and she has not opted out), so a
 * staff member should ring her personally. This follows ARMMAN's finding that a human call brings
 * disengaging mothers back.
 */
export async function getCallsOverview(facilityId: string, range: CallRange): Promise<CallsOverview> {
  const clinic = await loadClinic(facilityId);
  const motherById = new Map(clinic.mothers.map((m) => [m.id, m]));
  const babyByMother = new Map(clinic.babies.map((b) => [b.motherId, b]));
  const openFlagCalls = new Set(clinic.flags.filter((f) => f.status !== 'resolved' && f.callId).map((f) => f.callId as string));
  const today = startOfToday();
  const from = addDays(today, -RANGE_DAYS[range]);

  const known = clinic.calls.filter((c) => motherById.has(c.motherId));
  const inRange = known
    .filter((c) => dayDiff(from, parseDate(c.startedAt)) >= 0 && dayDiff(parseDate(c.startedAt), today) >= 0)
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt));

  const rows: CallRow[] = inRange.map((call) => {
    const openFlag = openFlagCalls.has(call.id);
    return { call, mother: motherById.get(call.motherId)!, baby: babyByMother.get(call.motherId), group: groupOf(call.outcome, openFlag), openFlag };
  });

  const answered = inRange.filter((c) => isAnswered(c.outcome)).length;
  const slots: SlotStat[] = SLOTS.map(({ slot, from: f, to }) => {
    const inSlot = inRange.filter((c) => {
      const h = new Date(c.startedAt).getHours();
      return h >= f && h < to;
    });
    return { slot, made: inSlot.length, answered: inSlot.filter((c) => isAnswered(c.outcome)).length };
  });

  // Drifting mothers look at all calls, not just the chosen range, so a quiet week doesn't hide them.
  const byMother = new Map<string, Call[]>();
  known.forEach((c) => byMother.set(c.motherId, [...(byMother.get(c.motherId) ?? []), c]));
  const drifting: DriftingMother[] = [];
  byMother.forEach((calls, motherId) => {
    const mother = motherById.get(motherId)!;
    if (mother.optedOut) return;
    const latestFirst = [...calls].sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    let missedInARow = 0;
    for (const c of latestFirst) {
      if (isAnswered(c.outcome)) break;
      missedInARow++;
    }
    if (missedInARow >= 2) drifting.push({ mother, baby: babyByMother.get(motherId), missedInARow, lastTried: latestFirst[0].startedAt });
  });
  drifting.sort((a, b) => b.missedInARow - a.missedInARow);

  return {
    made: inRange.length,
    answered,
    confirmed: inRange.filter((c) => c.outcome === 'confirmed').length,
    flagged: inRange.filter((c) => c.outcome === 'question_flagged').length,
    notReached: inRange.filter((c) => !isAnswered(c.outcome)).length,
    answerRate: inRange.length ? Math.round((answered / inRange.length) * 100) : null,
    slots,
    rows,
    drifting,
  };
}
